'use client';

import type React from 'react';
import { useState, useRef, type KeyboardEvent } from 'react';
import { Input } from '@/components/ui/input';
import { XIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
interface MultiEmailInputProps {
  emails: string[];
  setEmails: (emails: string[]) => void;
  placeholder?: string;
  className?: string;
}
export function MultiEmailInput({
  emails,
  setEmails,
  placeholder = 'Enter emails...',
  className
}: MultiEmailInputProps) {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const isValidEmail = (email: string): boolean => {
    // Basic email validation regex
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };
  const addEmailsFromInput = (input: string) => {
    const newEmails = input.split(/[\s,]+/) // Split by space or comma
    .map(email => email.trim()).filter(email => email && isValidEmail(email) && !emails.includes(email));
    if (newEmails.length > 0) {
      setEmails([...emails, ...newEmails]);
      setInputValue('');
    }
    // Don't clear input if no valid emails were added
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',' || event.key === ' ') {
      event.preventDefault();
      if (inputValue.trim()) {
        addEmailsFromInput(inputValue);
      }
    } else if (event.key === 'Backspace' && inputValue === '' && emails.length > 0) {
      event.preventDefault();
      setEmails(emails.slice(0, -1));
    }
  };
  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');
    addEmailsFromInput(pastedText);
  };
  const removeEmail = (indexToRemove: number) => {
    setEmails(emails.filter((_, index) => index !== indexToRemove));
  };
  const editEmail = (indexToEdit: number) => {
    const emailToEdit = emails[indexToEdit];
    setInputValue(emailToEdit);
    setEmails(emails.filter((_, index) => index !== indexToEdit));
    inputRef.current?.focus();
  };
  return <div className={cn('flex flex-wrap items-center gap-2 bg-background p-2 border-b', className)} onClick={() => inputRef.current?.focus()} data-sentry-component="MultiEmailInput" data-sentry-source-file="MultiEmailInput.tsx">
      {emails.map((email, index) => <span key={index} className="flex items-center gap-1.5 rounded-sm bg-accent px-2 py-1 text-sm text-primary cursor-pointer hover:bg-accent/80 transition-colors" onClick={e => {
      e.stopPropagation();
      editEmail(index);
    }} title="Click to edit">
          {email}
          <button type="button" onClick={e => {
        e.stopPropagation(); // Prevent editing when removing tag
        removeEmail(index);
      }} className="text-primary hover:text-primary/70" aria-label={`Remove ${email}`}>
            <XIcon className="h-3.5 w-3.5" />
          </button>
        </span>)}
      <Input ref={inputRef} type="text" // Changed from email to allow spaces/commas before processing
    value={inputValue} onChange={handleInputChange} onKeyDown={handleKeyDown} onPaste={handlePaste} placeholder={emails.length === 0 ? placeholder : ''} className="h-auto flex-grow border-none bg-transparent p-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 min-w-[150px]" data-sentry-element="Input" data-sentry-source-file="MultiEmailInput.tsx" />
    </div>;
}